// CSV Parser for data sources
import { SiteData, SimulationStatistic } from '../types';

const SITES_CSV_URL = 'https://docs.google.com/spreadsheets/d/182cA0vOxBOirWIeMR19zEEDMWMaufbrQGdhxEOJ0tkI/export?format=csv&gid=0';
const SIMULATION_STATS_CSV_URL = 'https://docs.google.com/spreadsheets/d/182cA0vOxBOirWIeMR19zEEDMWMaufbrQGdhxEOJ0tkI/export?format=csv&gid=357985648';

// Helper function to parse boolean values from CSV
const parseBoolean = (value: string | number): boolean => {
  if (typeof value === 'number') return value === 1;
  return value === '1' || value.toLowerCase() === 'true';
};

// Helper function to parse numbers from CSV
const parseNumber = (value: string | number): number => {
  if (typeof value === 'number') return value;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

// Helper function to clean and format strings
const cleanString = (value: string): string => {
  return value?.toString().trim() || '';
};

// Helper function to format dates
const formatDate = (dateStr: string): string => {
  if (!dateStr || dateStr.trim() === '') return '';
  try {
    const date = new Date(dateStr);
    return date.toISOString().split('T')[0];
  } catch {
    return dateStr;
  }
};

// Parse CSV row into SiteData object
const parseRowToSiteData = (row: string[]): SiteData | null => {
  if (row.length < 26) return null; // Not enough columns
  
  try {
    return {
      siteName: cleanString(row[0]),
      isOnline: parseBoolean(row[1]),
      customer: cleanString(row[2]),
      country: cleanString(row[3]),
      region: cleanString(row[4]),
      siteType: cleanString(row[5]),
      mlVersion: cleanString(row[6]),
      dbVersion: cleanString(row[7]),
      siteInstallDate: formatDate(row[8]),
      siteLicenseExpiration: formatDate(row[9]),
      siteLicenseExpirationInML: formatDate(row[10]),
      startUsage: cleanString(row[11]),
      lastUsage: cleanString(row[12]),
      monthsInUse: parseNumber(row[13]),
      usedLastMonth: parseBoolean(row[14]),
      simulatorsConnected: parseNumber(row[15]),
      registeredUsers: parseNumber(row[16]),
      activeUsers: parseNumber(row[17]),
      inactiveUsers: parseNumber(row[18]),
      numSessionsLastMonth: parseNumber(row[19]),
      numSessionsOnServer: parseNumber(row[20]),
      totalSimulationTimeMinutes: parseNumber(row[21]),
      numSessionsOnServerLast12Month: parseNumber(row[22]),
      simulationTimeMinutesLast12Month: parseNumber(row[23]),
      activeUsers12Months: parseNumber(row[24]),
      wRegion: cleanString(row[25]) // Column Z - Main region (US, Europe, APAC, South America)
    };
  } catch (error) {
    console.error('Error parsing row:', row, error);
    return null;
  }
};

// Parse CSV text into array of SiteData
const parseCSVText = (csvText: string): SiteData[] => {
  const lines = csvText.split('\n');
  const sites: SiteData[] = [];
  
  // Skip header row (first line)
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    // Parse CSV row (handle commas within quotes)
    const row: string[] = [];
    let currentField = '';
    let inQuotes = false;
    
    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        row.push(currentField);
        currentField = '';
      } else {
        currentField += char;
      }
    }
    
    // Add the last field
    if (currentField) {
      row.push(currentField);
    }
    
    const siteData = parseRowToSiteData(row);
    if (siteData && siteData.siteName) {
      sites.push(siteData);
    }
  }
  
  return sites;
};

// Fetch and parse site data
export const fetchGoogleSheetsData = async (): Promise<SiteData[]> => {
  try {
    console.log('Fetching site data...');
    const response = await fetch(SITES_CSV_URL);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const csvText = await response.text();
    const sites = parseCSVText(csvText);
    
    console.log(`Successfully parsed ${sites.length} sites from data source`);
    return sites;
  } catch (error) {
    console.error('Error fetching site data:', error);
    throw error;
  }
};

// Parse CSV row into SimulationStatistic object
const parseRowToSimulationStatistic = (row: string[]): SimulationStatistic | null => {
  console.log('Parsing row with', row.length, 'columns:', row);
  
  if (row.length < 10) {
    console.log('Row has insufficient columns, skipping');
    return null;
  }
  
  try {
    // Based on your sheet structure, assuming:
    // Column 0: Site Name
    // Column 1: Simulator Type 
    // Column 2: Module Description parts
    // Column J (9): Sessions Last 12 Months (this is what we want to use)
    // Column L (11): WRegion
    
    const result = {
      siteName: cleanString(row[0]),
      customer: '', // We'll derive this from site mapping or leave empty
      region: '', // We'll derive this from site mapping or leave empty  
      wRegion: cleanString(row[11]), // Column L (WRegion)
      simulatorType: cleanString(row[1]),
      moduleDescription: `${cleanString(row[2])}`.trim(),
      sessionsLastMonth: parseNumber(row[8]), // Column I
      sessionsLast12Months: parseNumber(row[9]) // Column J - this is what we want to use
    };
    
    console.log('Parsed simulation statistic:', result);
    return result;
  } catch (error) {
    console.error('Error parsing simulation stats row:', row, error);
    return null;
  }
};

// Parse CSV text into array of SimulationStatistic
const parseSimulationStatsCSVText = (csvText: string): SimulationStatistic[] => {
  const lines = csvText.split('\n');
  const stats: SimulationStatistic[] = [];
  
  // Skip header row (first line)
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    // Parse CSV row (handle commas within quotes)
    const row: string[] = [];
    let currentField = '';
    let inQuotes = false;
    
    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        row.push(currentField);
        currentField = '';
      } else {
        currentField += char;
      }
    }
    
    // Add the last field
    if (currentField) {
      row.push(currentField);
    }
    
    const statData = parseRowToSimulationStatistic(row);
    if (statData && statData.siteName) {
      stats.push(statData);
    }
  }
  
  return stats;
};

// Fetch and parse simulation statistics from data source
export const fetchSimulationStatistics = async (): Promise<SimulationStatistic[]> => {
  try {
    console.log('Fetching simulation statistics...');
    const response = await fetch(SIMULATION_STATS_CSV_URL);
    
    if (!response.ok) {
      console.error(`Simulation statistics sheet not accessible (${response.status}). NO FALLBACK - using empty array.`);
      return []; // Return empty array instead of mock data
    }
    
    const csvText = await response.text();
    console.log('Raw simulation statistics CSV text:', csvText.substring(0, 500) + '...');
    
    const stats = parseSimulationStatsCSVText(csvText);
    
    console.log(`Successfully parsed ${stats.length} simulation statistics`);
    console.log('First few simulation statistics:', stats.slice(0, 3));
    
    // Check for the specific CCF/BRONCH Mentor data the user mentioned
    const ccfBronchData = stats.filter(stat => 
      stat.siteName.includes('CCF') && 
      stat.simulatorType.includes('BRONCH')
    );
    console.log('CCF BRONCH data found:', ccfBronchData);
    
    return stats;
  } catch (error) {
    console.error('Error fetching simulation statistics:', error);
    console.log('ERROR: NO FALLBACK - returning empty array for simulation data');
    return []; // Return empty array instead of mock data
  }
};

// NO MOCK DATA - this function has been removed to force live data usage only

// Validate site data
export const validateSiteData = (sites: SiteData[]): { valid: SiteData[], invalid: any[] } => {
  const valid: SiteData[] = [];
  const invalid: any[] = [];
  
  sites.forEach(site => {
    if (!site.siteName || !site.customer || !site.region) {
      invalid.push(site);
    } else {
      valid.push(site);
    }
  });
  
  return { valid, invalid };
};